// src/ui/auth/AuthEntryScreen.jsx

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import filatomLogo from '../../assets/images/filatom_logo.png';
import authService, { GoogleSignInCancelledException } from '../../services/auth/authService';

export const AuthEntryScreen = () => {
  const navigate = useNavigate();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [toast, setToast] = useState(null); // { message }
  const toastTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const showToast = (message) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message });
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  };

  // ── Google sign-in ─────────────────────────────────────────────────────────

  const handleGoogleSignIn = async () => {
    if (isGoogleLoading) return;
    setIsGoogleLoading(true);

    try {
      const firebaseUser = await authService.signInWithGoogle();
      const profileExists = await authService.userProfileExists(firebaseUser.uid);

      if (profileExists) {
        navigate('/', { replace: true });
      } else {
        // Same onboarding destination the phone flow uses after OTP
        // verification — just without a phone number yet, so we collect
        // it on the next screen before Add Profile.
        navigate('/auth/google-phone', {
          state: {
            uid: firebaseUser.uid,
            email: firebaseUser.email || null,
            displayName: firebaseUser.displayName || null,
          },
        });
      }
    } catch (err) {
      if (err instanceof GoogleSignInCancelledException) {
        // User closed the popup — no need to show an error
      } else {
        console.error('[AuthEntry] Google sign-in error:', err);
        showToast('Google sign-in failed. Please try again.');
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  // ── Phone sign-in ────────────────────────────────────────────────────────

  const handlePhoneContinue = () => {
    navigate('/auth/phone');
  };

  const handleTerms = () => {
    navigate('/auth/terms-of-service');
  };

  const handlePrivacy = () => {
    navigate('/auth/privacy-policy');
  };

  return (
    <div className="min-h-screen bg-filatom-bg flex items-center justify-center px-4">
      {/* Desktop container - centered with max width */}
      <div className="w-full max-w-md mx-auto">
        <div className="flex flex-col items-center justify-center py-8">
          {/* App Logo */}
          <div className="w-48 md:w-56">
            <img
              src={filatomLogo}
              alt="Filatom Logo"
              className="w-full h-auto object-contain"
            />
          </div>

          <div className="w-full mt-8 flex flex-col gap-3">
            {/* Continue with Google */}
            <button
              onClick={handleGoogleSignIn}
              disabled={isGoogleLoading}
              className="w-full py-[16px] rounded-xl bg-white text-[#1F1F1F] text-base font-semibold flex items-center justify-center gap-3 transition-all hover:opacity-90 active:scale-98 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isGoogleLoading ? (
                <div className="w-5 h-5 border-2 border-[#1F1F1F] border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <GoogleIcon className="w-5 h-5" />
                  <span>Continue with Google</span>
                </>
              )}
            </button>

            {/* Continue with Phone */}
            <button
              onClick={handlePhoneContinue}
              className="w-full py-[16px] rounded-xl text-white text-base font-semibold border border-white/15 transition-all hover:bg-white/[0.05] active:scale-98"
            >
              Continue with Phone
            </button>
          </div>

          {/* Terms and Conditions */}
          <div className="mt-6 px-4 text-center">
            <p className="text-white/50 text-[13px] leading-relaxed">
              By Continuing to Filatom, you agree to Filatom's{' '}
              <button
                onClick={handleTerms}
                className="text-filatom-primary hover:underline focus:outline-none"
              >
                Terms and Conditions
              </button>
              {' and '}
              <button
                onClick={handlePrivacy}
                className="text-filatom-primary hover:underline focus:outline-none"
              >
                Privacy Policy
              </button>
            </p>
          </div>

          <div className="mt-10" />
        </div>
      </div>

      {/* Snackbar-style toast (mirrors PhoneAuthPage) */}
      {toast && (
        <div className="fixed left-4 right-4 bottom-6 max-w-md mx-auto z-50">
          <div
            className="rounded-xl px-4 py-3 shadow-lg"
            style={{ backgroundColor: '#2C1A0E' }}
          >
            <p className="text-white text-sm text-center">{toast.message}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Standard Google "G" mark, per Google's sign-in button brand guidelines
const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 01-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
  </svg>
);

export default AuthEntryScreen;